import React, { useEffect } from 'react';
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from 'react-router-dom';
import { format } from "date-fns";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { toast } from "@/components/ui/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { Spinner } from '@/components/common/Spinner';

const formSchema = z.object({
  supervisor_name: z.string().min(2, "Supervisor name must be at least 2 characters"),
  date: z.date({
    required_error: "A date is required",
  }),
  duration: z.number().min(1, "Duration must be at least 1 minute"),
  topics: z.string().min(2, "Topics are required"),
  notes: z.string().optional(),
});

interface SupervisionSession {
  id: string;
  supervisor_name: string;
  date: string;
  duration: number;
  topics: string[];
  notes: string;
  follow_up?: string;
  user_id: string;
}

interface SupervisionFormProps {
  sessionToEdit?: SupervisionSession | null;
  isLoading?: boolean;
}

export const SupervisionForm: React.FC<SupervisionFormProps> = ({ sessionToEdit, isLoading = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!sessionToEdit;
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      supervisor_name: "",
      topics: "",
      notes: "",
      duration: 60,
    },
  });

  // Set form values when editing an existing session
  useEffect(() => {
    if (sessionToEdit) {
      form.reset({
        supervisor_name: sessionToEdit.supervisor_name,
        date: new Date(sessionToEdit.date),
        duration: sessionToEdit.duration,
        topics: sessionToEdit.topics ? sessionToEdit.topics.join(', ') : '',
        notes: sessionToEdit.notes || '',
      });
    }
  }, [sessionToEdit, form]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    try {
      // Get the current user's session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) throw sessionError;
      if (!sessionData.session || !sessionData.session.user) {
        toast({
          title: "Authentication Error",
          description: "You must be logged in to create a supervision session",
          variant: "destructive",
        });
        navigate('/login');
        return;
      }

      const userId = sessionData.session.user.id;

      if (isEditMode && id) {
        // Update existing session
        const { error } = await supabase
          .from('supervision_sessions')
          .update({
            supervisor_name: data.supervisor_name,
            date: data.date.toISOString(),
            duration: data.duration,
            topics: data.topics.split(',').map(topic => topic.trim()),
            notes: data.notes || '',
            updated_at: new Date().toISOString(),
          })
          .eq('id', id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Supervision session updated successfully",
        });
      } else {
        // Create new session
        const { error } = await supabase
          .from('supervision_sessions')
          .insert({
            supervisor_name: data.supervisor_name,
            date: data.date.toISOString(),
            duration: data.duration,
            topics: data.topics.split(',').map(topic => topic.trim()),
            notes: data.notes || '',
            user_id: userId,
          });

        if (error) throw error;

        toast({
          title: "Success",
          description: "Supervision session created successfully",
        });
      }
      
      navigate('/supervision');
    } catch (error) {
      console.error('Error saving supervision session:', error);
      toast({
        title: "Error",
        description: `Could not ${isEditMode ? 'update' : 'create'} supervision session`,
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-coaching-dark">
            {isEditMode ? 'Edit' : 'New'} Supervision Session
          </h1>
          <p className="text-muted-foreground">
            {isEditMode ? 'Update' : 'Record'} a supervision session
          </p>
        </div>
        <Button 
          onClick={() => navigate('/supervision')}
          variant="outline"
        >
          Cancel
        </Button>
      </div>

      <div className="bg-white p-6 rounded-md shadow">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="supervisor_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Supervisor Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter supervisor name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-[240px] pl-3 text-left font-normal text-foreground",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value ? (
                            format(field.value, "PPP")
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="duration"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Duration (minutes)</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      {...field} 
                      onChange={(e) => field.onChange(parseInt(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="topics"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Topics (comma-separated)</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter topics, separated by commas" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Add any additional notes" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => navigate('/supervision')}
                className="border-gray-300 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              >
                Cancel
              </Button>
              <Button type="submit">{isEditMode ? 'Update' : 'Save'} Session</Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};
